import React, { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";

export default function QRScannerCustom({ onResult }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const [facingMode, setFacingMode] = useState("environment"); // or "user"
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [detected, setDetected] = useState(false);
  const lastQRRef = useRef(null);

  useEffect(() => {
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function startCamera() {
    setError(null);
    stopCamera();
    try {
      const constraints = {
        audio: false,
        video: {
          facingMode, // "environment" requests back camera
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setRunning(true);
      setDetected(false);
      lastQRRef.current = null;
      tick(); // start frame processing loop
    } catch (err) {
      console.error("Camera start error", err);
      setError(err.message || String(err));
      setRunning(false);
    }
  }

  function stopCamera() {
    setRunning(false);
    cancelAnimationFrame(rafRef.current);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setDetected(false);
  }

  function playBeep() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = 880;
      gain.gain.value = 0.1;
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.15);
      oscillator.onended = () => ctx.close();
    } catch (e) {
      // Ignore audio errors
    }
  }

  function tick() {
    rafRef.current = requestAnimationFrame(tick);
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || video.readyState !== HTMLMediaElement.HAVE_ENOUGH_DATA) return;
    const ctx = canvas.getContext("2d");

    // keep canvas same aspect as video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    // jsQR expects Uint8ClampedArray (imageData.data) and image dims
    const code = jsQR(imageData.data, canvas.width, canvas.height, {
      inversionAttempts: "attemptBoth"
    });

    if (code) {
      // Only beep and call onResult for new QR code
      if (lastQRRef.current !== code.data) {
        playBeep();
        setDetected(true);
        lastQRRef.current = code.data;
        onResult && onResult(code.data);
        setTimeout(() => stopCamera(), 400); // auto-stop after scan
      }
      drawBoundingBox(ctx, code.location);
    } else {
      setDetected(false);
    }
  }

  function drawBoundingBox(ctx, loc) {
    if (!loc) return;
    ctx.strokeStyle = "#4caf50";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(loc.topLeftCorner.x, loc.topLeftCorner.y);
    ctx.lineTo(loc.topRightCorner.x, loc.topRightCorner.y);
    ctx.lineTo(loc.bottomRightCorner.x, loc.bottomRightCorner.y);
    ctx.lineTo(loc.bottomLeftCorner.x, loc.bottomLeftCorner.y);
    ctx.closePath();
    ctx.stroke();
  }

  async function toggleTorch() {
    // Torch control is device/browsers dependent.
    try {
      const track = streamRef.current?.getVideoTracks()[0];
      if (!track) return;
      const imageCapture = new window.ImageCapture && new window.ImageCapture(track);
      if (!imageCapture || !("getPhotoCapabilities" in imageCapture)) {
        // fallback: try applyConstraints
        await track.applyConstraints({ advanced: [{ torch: true }] });
      } else {
        const caps = await imageCapture.getPhotoCapabilities();
        if (caps.fillLightMode.includes("torch")) {
          await track.applyConstraints({ advanced: [{ torch: true }] });
        } else {
          alert("Torch not supported on this device/browser");
        }
      }
    } catch (e) {
      console.warn("Torch toggle failed", e);
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <button onClick={() => { setFacingMode("environment"); startCamera(); }} disabled={running}>
          Start (back)
        </button>
        <button onClick={() => { setFacingMode("user"); startCamera(); }} disabled={running} style={{ marginLeft: 8 }}>
          Start (front)
        </button>
        <button onClick={stopCamera} disabled={!running} style={{ marginLeft: 8 }}>
          Stop
        </button>
        <button onClick={toggleTorch} style={{ marginLeft: 8 }}>
          Toggle Torch (if supported)
        </button>
      </div>

      {error && <div style={{ color: "crimson" }}>Error: {error}</div>}

      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 420,
          border: `3px solid ${detected ? "#4caf50" : "#888"}`,
          borderRadius: 8,
          transition: "border-color 0.2s"
        }}
      >
        <video
          ref={videoRef}
          style={{ width: "100%", height: "auto", borderRadius: 8, background: "#000" }}
          playsInline
          muted
        />
        <canvas ref={canvasRef} style={{ display: "none" }} />
        {detected && (
          <div
            style={{
              position: "absolute",
              top: 10,
              left: 10,
              background: "#4caf50",
              color: "#fff",
              padding: "2px 10px",
              borderRadius: 4,
              fontWeight: "bold",
              fontSize: 14
            }}
          >
            QR détecté !
          </div>
        )}
      </div>
    </div>
  );
}
